import React, { useMemo, useState } from 'react';
import { Plus, UserPlus, Copy, Check, Download } from 'lucide-react';
import Card, { CardHeader } from '../components/ui/Card';
import Button from '../components/ui/Button';
import EntityTable from '../components/shared/EntityTable';
import EntityModal from '../components/modals/EntityModal';
import { useAppStore } from '../store';
import { Client } from '../types';
import { supabase } from '../lib/supabase';
import { getClientAccountBalances, getClientOverdueBalances } from '../utils/clientAccountBalance';
import { formatCurrencyForCSV } from '../utils/formatters';
import { objectsToCsv, downloadCsv } from '../utils/csvUtils';

const Clients: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isPortalModalOpen, setIsPortalModalOpen] = useState(false);
  const [portalClientId, setPortalClientId] = useState<string>('');
  const [portalEmail, setPortalEmail] = useState<string>('');
  const [portalNombre, setPortalNombre] = useState<string>('');
  const [portalEmpresa, setPortalEmpresa] = useState<string>('');
  const [isCreatingPortal, setIsCreatingPortal] = useState(false);
  const [tempPassword, setTempPassword] = useState<string>('');
  const [copied, setCopied] = useState(false);
  
  const { clients, addClient, currentUser, salespersons, invoices, payments } = useAppStore();
  
  // Filtrado por vendedor solo en esta pantalla
  let filteredClients = clients.filter(client => 
    client.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (client.email && client.email.toLowerCase().includes(searchQuery.toLowerCase()))
  );

  // Solo filtrar si el usuario es vendedor
  const userRole = currentUser?.role ? currentUser.role.trim().toLowerCase() : '';
  if (userRole === 'vendedor') {
    // Find the current user's name from salespersons
    const currentSalesperson = salespersons.find(sp => 
      sp.email.toLowerCase() === currentUser.email?.toLowerCase()
    );
    
    if (currentSalesperson) {
      filteredClients = filteredClients.filter(client => 
        client.salesperson === currentSalesperson.name
      );
    }
  }
  
  const handleAddClient = () => {
    setIsModalOpen(true);
  };

  const handleExportClientsCsv = () => {
    const rows = filteredClients.map((client) => {
      const balance = getClientAccountBalances(client.id, invoices, payments);
      const overdue = getClientOverdueBalances(client.id, invoices, payments);
      return {
        Cliente: client.name || '',
        Email: client.email || '',
        Teléfono: (client as any).phone || '',
        Vendedor: (client as any).salesperson || '',
        'Saldo actual ARS': formatCurrencyForCSV(balance.ars),
        'Saldo actual USD': formatCurrencyForCSV(balance.usd),
        'Saldo vencido ARS': formatCurrencyForCSV(overdue.ars),
        'Saldo vencido USD': formatCurrencyForCSV(overdue.usd),
      };
    });

    const headers = [
      'Cliente',
      'Email',
      'Teléfono',
      'Vendedor',
      'Saldo actual ARS',
      'Saldo actual USD',
      'Saldo vencido ARS',
      'Saldo vencido USD',
    ];
    const csv = objectsToCsv(rows, headers);
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(csv, `cuentas-clientes-${stamp}.csv`);
  };

  const openPortalModal = () => {
    // Prefill con primer cliente filtrado si hay
    const first = filteredClients[0];
    if (first) {
      setPortalClientId(first.id);
      setPortalEmail(first.email || '');
      setPortalNombre(first.name || '');
      setPortalEmpresa(first.company || '');
    }
    setIsPortalModalOpen(true);
  };
  
  const handleSaveClient = async (newClient: Omit<Client, 'id' | 'balance'>) => {
    await addClient(newClient);
    setIsModalOpen(false);
  };

  const selectedPortalClient = useMemo(() => {
    return clients.find(c => c.id === portalClientId) || null;
  }, [clients, portalClientId]);

  const handlePortalClientChange = (id: string) => {
    setPortalClientId(id);
    const c = clients.find(x => x.id === id);
    if (c) {
      setPortalEmail(c.email || '');
      setPortalNombre(c.name || '');
      // @ts-ignore optional field in store type
      setPortalEmpresa((c as any).company || '');
    }
  };

  const handleCreatePortalAccount = async () => {
    if (!portalClientId || !portalEmail) return;
    setIsCreatingPortal(true);
    setTempPassword('');
    setCopied(false);
    try {
      const { data, error } = await supabase.rpc('admin_create_portal_account_for_client', {
        p_internal_client_id: portalClientId,
        p_email: portalEmail,
        p_nombre: portalNombre || null,
        p_empresa: portalEmpresa || null
      });
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : null;
      if (row) {
        setTempPassword(row.temp_password || '');
      }
    } catch (e) {
      console.error('Error creando cuenta de portal:', e);
      alert('No se pudo crear la cuenta de portal. Revisa la consola para más detalles.');
    } finally {
      setIsCreatingPortal(false);
    }
  };
  
  const handleViewDetails = (id: string) => {
    // This function is handled in EntityTable
  };
  
  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className="flex flex-col lg:flex-row gap-4">
        {/* Search Bar */}
        <div className="relative flex-1">
          <input
            type="text"
            placeholder="Buscar clientes..."
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <svg className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 lg:flex-shrink-0">
          <Button 
            variant="primary" 
            icon={<Plus className="h-4 w-4" />}
            onClick={handleAddClient}
            className="w-full sm:w-auto"
          >
            Nuevo Cliente
          </Button>

          <Button 
            variant="outline" 
            icon={<UserPlus className="h-4 w-4" />}
            onClick={openPortalModal}
            className="w-full sm:w-auto"
          >
            Crear Portal
          </Button>

          <Button
            variant="outline"
            icon={<Download className="h-4 w-4" />}
            onClick={handleExportClientsCsv}
            className="w-full sm:w-auto"
          >
            Exportar CSV
          </Button>
        </div>
      </div>
      
      <Card>
        <CardHeader title={`Clientes (${filteredClients.length})`} />
        
        <EntityTable
          data={filteredClients}
          entityType="client"
          onViewDetails={handleViewDetails}
          hideFinancialColumns={userRole === 'vendedor'}
          defaultSortField={userRole === 'vendedor' ? 'name' : 'balance'}
          defaultSortDirection={userRole === 'vendedor' ? 'asc' : 'desc'}
        />
      </Card>

      <EntityModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        entityType="client"
        onSave={handleSaveClient}
      />

      {/* Modal simple para crear cuenta de portal */}
      {isPortalModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg bg-white rounded-lg shadow-lg p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Crear cuenta de portal</h3>
              <button className="text-gray-500 hover:text-gray-700 p-1" onClick={() => setIsPortalModalOpen(false)}>✕</button>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cliente</label>
                <select
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  value={portalClientId}
                  onChange={(e) => handlePortalClientChange(e.target.value)}
                >
                  {filteredClients.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  value={portalEmail}
                  onChange={(e) => setPortalEmail(e.target.value)}
                  placeholder="cliente@correo.com"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={portalNombre}
                    onChange={(e) => setPortalNombre(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Empresa</label>
                  <input
                    type="text"
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    value={portalEmpresa}
                    onChange={(e) => setPortalEmpresa(e.target.value)}
                  />
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 mt-6">
                <Button 
                  variant="outline" 
                  onClick={() => setIsPortalModalOpen(false)}
                  className="w-full sm:w-auto"
                >
                  Cancelar
                </Button>
                <Button 
                  variant="primary" 
                  onClick={handleCreatePortalAccount} 
                  disabled={isCreatingPortal}
                  className="w-full sm:w-auto"
                >
                  {isCreatingPortal ? 'Creando...' : 'Crear cuenta'}
                </Button>
              </div>

              {tempPassword && (
                <div className="mt-4 p-4 border border-green-200 bg-green-50 rounded-md">
                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-700">Cuenta creada para <span className="font-medium">{portalEmail}</span></p>
                      <p className="text-sm text-gray-700 mt-1">Contraseña temporal:</p>
                      <code className="block px-3 py-2 bg-white border rounded text-sm select-all mt-2 break-all">{tempPassword}</code>
                    </div>
                    <button
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2 text-sm border border-green-300 bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors"
                      onClick={() => { navigator.clipboard.writeText(tempPassword); setCopied(true); setTimeout(()=>setCopied(false), 1500); }}
                    >
                      {copied ? <Check className="h-4 w-4"/> : <Copy className="h-4 w-4"/>}
                      {copied ? '¡Copiado!' : 'Copiar contraseña'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Clients;